import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DateRange } from 'react-date-range';      // ⬅️  same picker you already use
import clsx from 'clsx';
import formatDate from './../utils/formatDate';
import dashboardService from './../services/DashboardService';
import { useAuth } from './../context/AuthContext';

const Th = ({ col, current, dir, setCol, setDir, children, numeric }) => {
  const active = current === col;
  const nextDir = active && dir === 'ASC' ? 'DESC' : 'ASC';

  return (
    <th
      className={clsx(
        'select-none px-4 py-3',
        numeric && 'text-right',
        'cursor-pointer hover:bg-slate-100'
      )}
      onClick={() => {
        setCol(col);
        setDir(nextDir);
      }}
    >
      {children}
      {active && (dir === 'ASC' ? ' ▲' : ' ▼')}
    </th>
  );
};

Th.propTypes = {
  col: PropTypes.number.isRequired,
  current: PropTypes.number.isRequired,
  dir: PropTypes.oneOf(['ASC', 'DESC']).isRequired,
  setCol: PropTypes.func.isRequired,
  setDir: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  numeric: PropTypes.bool,
};

function KpiDashboard() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { token } = useAuth();
  const today = new Date();
  const startOfDay = new Date(today.setHours(0, 0, 0, 0));
  const endOfDay = new Date();
  endOfDay.setHours(23, 59, 59, 999);
  
  const [dateRange, setDateRange] = useState([
    {
      startDate: startOfDay,
      endDate: endOfDay,
      key: 'selection',
    },
  ]);

  const [startTime, setStartTime] = useState('00:00');
  const [endTime, setEndTime] = useState('23:59');
  const [sortColumn, setSortColumn] = useState(1);
  const [sortDirection, setSortDirection] = useState('ASC');

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const filters = {
        dateRange,
        startTime,
        endTime,
        sortColumn,
        sortDirection,
      };

      const result = await dashboardService.getRecords(filters);
      setData(result || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();    
  }, [dateRange, startTime, endTime, sortColumn, sortDirection]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <span className="animate-spin rounded-full border-4 border-slate-300 border-t-slate-600 h-8 w-8" />
        <span className="ml-3 text-sm text-slate-600">Loading records…</span>
      </div>
    );
  }



  return (
  <section className="space-y-6">
   

    {/* -------- filters -------------------------------------------------- */}
    <div className="flex flex-wrap items-end gap-4">
      <DateRange
        ranges={dateRange}
        onChange={item => setDateRange([item.selection])}
        maxDate={new Date()}
      />

      <div className="flex flex-col">
        <label className="text-xs font-medium text-slate-600" htmlFor="start">
          Start&nbsp;time
        </label>
        <input
          id="start"
          type="time"
          value={startTime}
          onChange={e => setStartTime(e.target.value)}
          className="rounded border px-2 py-1 text-sm"
        />
      </div>
      <div className="flex flex-col">
        <label className="text-xs font-medium text-slate-600" htmlFor="end">
          End&nbsp;time
        </label>
        <input
          id="end"
          type="time"
          value={endTime}
          onChange={e => setEndTime(e.target.value)}
          className="rounded border px-2 py-1 text-sm"
        />
      </div>

      <button
        className="ml-auto rounded bg-slate-700 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
        onClick={fetchData}
      >
        Refresh
      </button>
    </div>

      {/* -------- table ---------------------------------------------------- */}
      <div className="overflow-x-auto rounded-lg shadow">
        <table className="min-w-full divide-y divide-gray-200 bg-white">
          <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
            <tr>
              <Th
                col={1}
                current={sortColumn}
                dir={sortDirection}
                setCol={setSortColumn}
                setDir={setSortDirection}
              >
                User
              </Th>
              <Th
                col={2}
                current={sortColumn}
                dir={sortDirection}
                setCol={setSortColumn}
                setDir={setSortDirection}
                numeric
              >
                Reviewed
              </Th>
              <Th
                col={3}
                current={sortColumn}
                dir={sortDirection}
                setCol={setSortColumn}
                setDir={setSortDirection}
                numeric
              >
                Not&nbsp;Reviewed
              </Th>
              <Th
                col={4}
                current={sortColumn}
                dir={sortDirection}
                setCol={setSortColumn}
                setDir={setSortDirection}
                numeric
              >
                Total
              </Th>
              <th className="px-4 py-3 text-right">%&nbsp;Reviewed</th>
              <th className="px-4 py-3">Period&nbsp;Start</th>
              <th className="px-4 py-3">Period&nbsp;End</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100 text-sm">
            {data.map((row, idx) => {
              const total =
                row.totalReviewedRecords + row.totalNotReviewedRecords;
              const pct = total
                ? (row.totalReviewedRecords / total) * 100
                : 0;

              return (
                <tr
                  key={idx}
                  className={clsx(idx % 2 ? 'bg-gray-50' : 'bg-white')}
                >
                  <td className="whitespace-nowrap px-4 py-2 font-medium text-gray-900">
                    {row.userName || 'Unknown / System'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                    {row.totalReviewedRecords.toLocaleString()}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                    {row.totalNotReviewedRecords.toLocaleString()}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                    {total.toLocaleString()}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                    {pct.toFixed(1)}%
                  </td>
                  <td className="whitespace-nowrap px-4 py-2">
                    {formatDate(row.fromDate)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2">
                    {formatDate(row.toDate)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}


export default KpiDashboard;
